import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import FavouritesModal from "./FavouritesModal";

// Icons
import helpdeskIcon from "./assets/helpdesk-icon.svg";
import factoryIcon from "./assets/factory.png";
import warehouseIcon from "./assets/warehouse-icon.svg";
import driverIcon from "./assets/van.png";
import salesIcon from "./assets/sales.png";
import adminIcon from "./assets/admin-icon.png";
import returnIcon from "./assets/product-return.png";
import maintenanceIcon from "./assets/wrench.png";
import liamIcon from "./assets/liampeter.png";

// Map each widget to allowed roles (admin auto-all)
const WIDGETS = [
  { title: "Help Desk",          icon: helpdeskIcon,  to: "/HelpDeskDashboard",      roles: ["office","sales","admin"] },
  { title: "Warehouse",          icon: warehouseIcon, to: "/WarehouseDashboard",     roles: ["warehouse","admin"] },
  { title: "Stock Take",         icon: warehouseIcon, to: "/StocktakeDashboard",     roles: ["stocktake","admin"] },
  { title: "Despatch",           icon: driverIcon,    to: "/DespatchDashboard",      roles: ["despatch","admin"] },
  { title: "Returns",            icon: returnIcon,    to: "/ReturnsDashboard",       roles: ["returns","admin"] },  
  { title: "Maintenance",        icon: maintenanceIcon,to: "/MaintenanceDashboard",  roles: ["maintenance","operator","admin"] },
  { title: "Sales",              icon: salesIcon,     to: "/SalesDashboard",         roles: ["sales","admin"] },
  { title: "Admin Panel",        icon: adminIcon,     to: "/AdminDashboard",         roles: ["admin"] },
  { title: "Liam & Peter's place",icon: liamIcon,     to: "/CylInfoSearch",          roles: ["liam","admin"] },
  { title: "Fitting Shop",       icon: factoryIcon,   to: "/FittingDashboard",       roles: ["fitting"] },
  { title: "Production Dashboard",icon: factoryIcon,  to: "/ProductionDashboard",    roles: ["production","stretch","manager","admin"] },
  { title: "Maintenance Manager",        icon: maintenanceIcon,to: "/MaintenanceDashboardSec",  roles: ["maintmanager","admin"] },
  { title: "Engineers",        icon: adminIcon,to: "/EngineerDashboard",  roles: ["maintenance","admin"] },
  { title: "Sales Warehouse",    icon: salesIcon,     to: "/SalesRepStock",          roles: ["salesrep","admin"] },
];

const Dashboard = () => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const roles = useMemo(() => profile?.roles ?? [], [profile]);
  const isAdmin = roles.includes("admin");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setProfile(null); setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, "users", user.uid)); // new: users/{uid}
        setProfile(snap.exists() ? snap.data() : null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [auth, db]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const visibleWidgets = useMemo(() => {
    if (isAdmin) return WIDGETS; // admin sees everything
    return WIDGETS.filter(w => w.roles.some(r => roles.includes(r)));
  }, [roles, isAdmin]);

  return (
    <div>
      {/* Header */}
      <div className="bg-primary text-white p-6 flex justify-between items-center shadow-md">
        <h1 className="text-2xl font-bold">
          {profile?.name ? `${profile.name}'s Dashboard` : "Dashboard"}
        </h1>
        <div className="space-x-4">
          <button
            onClick={() => setIsModalOpen(v => !v)}
            className="bg-gray-200 text-primary px-4 py-2 rounded-lg hover:bg-gray-300 transition"
          >
            Favourites
          </button>
          <button
            onClick={handleLogout}
            className="bg-red-500 px-4 py-2 rounded-lg hover:bg-red-600 transition"
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <FavouritesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          favourites={profile?.favourites ?? []}
        />
      )}

      {/* Widget Grid */}
      <div className="p-6">
        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {visibleWidgets.length === 0 && (
              <div className="text-gray-500 col-span-full">
                No sections available for your role.
              </div>
            )}
            {visibleWidgets.map(w => (
              <Widget
                key={w.title}
                title={w.title}
                icon={w.icon}
                onClick={() => navigate(w.to)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Widget
const Widget = ({ title, icon, onClick }) => (
  <div
    onClick={onClick}
    className="flex flex-col items-center bg-white p-4 rounded-lg shadow-md hover:shadow-xl transition cursor-pointer transform hover:-translate-y-1"
  >
    <img src={icon} alt={title} className="h-12 w-12" />
    <p className="mt-2 font-semibold">{title}</p>
  </div>
);

export default Dashboard;
